import React, { useContext, useEffect, useRef } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import MailOutline from "@mui/icons-material/MailOutline";
import Schedule from "@mui/icons-material/Schedule";
import { Link } from "react-router-dom";
import { AppContext } from "contexts/AppContext";
import Translate from "utils/Translate";

const NewRegisterConfirmation = ({ automaticApproval, mobilePhoneNumber }) => {
    const { styleGuide } = useContext(AppContext);
    const confirmationRef = useRef(null);

    useEffect(() => {
        confirmationRef.current.focus();
    }, []);

    return (
        <Stack
            ref={confirmationRef}
            tabIndex={-1}
            role="status"
            data-cy="register.sucess"
            sx={{
                gap: "var(--auth-field-gap)",
                p: "var(--auth-panel-padding)",
                border: "1px solid #dce8e1",
                borderRadius: "18px",
                background: "linear-gradient(145deg, #f1f8f4, #fff 65%)",
                outline: "none",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 56, height: 56, borderRadius: "16px", bgcolor: "#e1f1e7", color: "#28734c" }}>
                <CheckCircleOutline sx={{ fontSize: 32 }} />
            </Box>
            <Box>
                <Chip
                    size="small"
                    label={<Translate translationKey={automaticApproval ? "_REGISTER_STATUS_READY_" : "_REGISTER_STATUS_RECEIVED_"}>
                        {automaticApproval ? "Klar til login" : "Anmodning modtaget"}
                    </Translate>}
                    sx={{ mb: 1.5, bgcolor: "#e1f1e7", color: "#285c40", fontFamily: "inherit", fontWeight: 500 }}
                />
                <Typography component="h2" sx={{ mb: 1, fontFamily: styleGuide.fontFamily, fontSize: "var(--auth-subtitle-size)", fontWeight: 600, lineHeight: 1.3, color: "#26384a" }}>
                    <Translate translationKey={automaticApproval ? "_REGISTER_CREATED_HEADLINE_" : "_REGISTER_SUCCESS_HEADLINE_"}>
                        {automaticApproval ? "Din bruger er oprettet" : "Tak. Din anmodning er nu sendt."}
                    </Translate>
                </Typography>
                <Typography sx={{ fontFamily: "inherit", fontSize: "var(--auth-copy-size)", lineHeight: 1.7, color: "#5c6673" }}>
                    <Translate translationKey={automaticApproval ? "_REGISTER_CREATED_TEXT_" : "_REGISTER_SUCCESS_TEXT_"}>
                        {automaticApproval
                            ? "Du kan nu logge ind med din email eller dit mobilnummer."
                            : "Før du kan logge ind skal en administrator godkende din bruger. Du modtager en email når dette er sket."}
                    </Translate>
                </Typography>
            </Box>
            {!automaticApproval && (
                <Stack component="ol" spacing={2} sx={{ m: 0, p: 2, listStyle: "none", borderRadius: "12px", bgcolor: "#f5f7f9" }}>
                    <Stack component="li" direction="row" spacing={1.5}>
                        <Schedule sx={{ mt: 0.25, color: "#687482", fontSize: 20 }} />
                        <Typography sx={{ fontFamily: "inherit", fontSize: 14, lineHeight: 1.5 }}>
                            <Translate translationKey="_REGISTER_NEXT_STEP_APPROVAL_">En administrator gennemgår din anmodning.</Translate>
                        </Typography>
                    </Stack>
                    <Stack component="li" direction="row" spacing={1.5}>
                        <MailOutline sx={{ mt: 0.25, color: "#687482", fontSize: 20 }} />
                        <Typography sx={{ fontFamily: "inherit", fontSize: 14, lineHeight: 1.5 }}>
                            <Translate translationKey="_REGISTER_NEXT_STEP_EMAIL_">Hold øje med din indbakke. Vi sender dig en e-mail, når du kan logge ind.</Translate>
                        </Typography>
                    </Stack>
                </Stack>
            )}
            <Button
                component={Link}
                to={automaticApproval ? `/login?identifier=${encodeURIComponent(mobilePhoneNumber)}` : "/login"}
                variant={automaticApproval ? "contained" : "outlined"}
                disableElevation
                sx={{
                    alignSelf: "flex-start",
                    minHeight: 44,
                    px: 3,
                    borderRadius: "10px",
                    fontFamily: "inherit",
                    fontWeight: 600,
                    textTransform: "none",
                    ...(automaticApproval ? {
                        bgcolor: styleGuide.color16,
                        color: styleGuide.color1,
                        "&:hover": { bgcolor: styleGuide.color17 },
                    } : { color: "#465567", borderColor: "#c9d2dc", "&:hover": { borderColor: "#465567", bgcolor: "#f5f7f9" } }),
                }}
            >
                <Translate translationKey={automaticApproval ? "_LOGIN_PAGE_LOGIN_TEXT_" : "_REGISTER_BACK_TO_LOGIN_"}>
                    {automaticApproval ? "Log ind" : "Tilbage til login"}
                </Translate>
            </Button>
        </Stack>
    );
};

export default NewRegisterConfirmation;
